import { useEffect, useState } from 'react';
import type { Seat } from '../../../shared/domain.ts';
import { MatchReviewError, fetchMatchReview } from '../../lib/api.ts';
import type { MatchReview, ReviewFailure } from '../../lib/api.ts';
import { ReviewPage } from './ReviewPage.tsx';

export interface ReviewScreenProps {
  readonly matchId: string;
  readonly yourSeat?: Seat | null;
  readonly onExit: () => void;
  readonly exitLabel?: string;
  readonly onRematch?: (() => void) | undefined;
  readonly budgetMs?: number | undefined;
}

interface Loading {
  readonly status: 'loading';
}
interface Loaded {
  readonly status: 'ready';
  readonly review: MatchReview;
}
interface Failed {
  readonly status: 'failed';
  readonly reason: ReviewFailure;
  readonly message: string;
}

/** What went wrong, said in the way each case actually needs saying. */
const FAILURE_TEXT: Record<ReviewFailure, string> = {
  not_found: 'There is no match with that link. It may have been from a different server.',
  still_in_play: 'That match is still being played. The review opens when the game ends.',
  unavailable: 'The review could not be loaded. Try again in a moment.',
};

/**
 * Fetches a finished match and hands it to the review.
 *
 * The log comes from the server rather than from whatever the client happened
 * to have in memory: a reload, a reconnect or a link from a friend must all
 * produce the same complete game, and a review of half a match would be worse
 * than no review at all.
 */
export function ReviewScreen({
  matchId,
  yourSeat = null,
  onExit,
  exitLabel = 'Back to lobby',
  onRematch,
  budgetMs,
}: ReviewScreenProps) {
  const [state, setState] = useState<Loading | Loaded | Failed>({ status: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: 'loading' });

    fetchMatchReview(matchId, controller.signal)
      .then((review) => {
        setState({ status: 'ready', review });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        const reason = error instanceof MatchReviewError ? error.reason : 'unavailable';
        setState({ status: 'failed', reason, message: FAILURE_TEXT[reason] });
      });

    return () => {
      controller.abort();
    };
  }, [matchId]);

  if (state.status === 'loading') {
    return (
      <section className="review review--loading" aria-labelledby="review-heading">
        <h2 id="review-heading" className="review__heading">
          Game review
        </h2>
        <p className="review__progress" role="status">
          Loading the game…
        </p>
        <div className="review__skeleton" aria-hidden="true">
          <span className="review__skeletonBoard" />
          <span className="review__skeletonRail" />
        </div>
      </section>
    );
  }

  if (state.status === 'failed') {
    return (
      <section className="review review--failed" aria-labelledby="review-heading">
        <h2 id="review-heading" className="review__heading">
          Game review
        </h2>
        <p className="review__notice">{state.message}</p>
        <footer className="review__actions">
          <button type="button" className="button button--quiet" onClick={onExit}>
            {exitLabel}
          </button>
        </footer>
      </section>
    );
  }

  return (
    <ReviewPage
      review={state.review}
      yourSeat={yourSeat}
      onExit={onExit}
      exitLabel={exitLabel}
      onRematch={onRematch}
      budgetMs={budgetMs}
    />
  );
}
